import React, { useState, useEffect } from "react";
import { PieChart, Pie, Sector, ResponsiveContainer, Cell } from "recharts";
import {
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
} from "@mui/material";
import unsplash from "../api/unsplash";
import { Circle } from "@mui/icons-material";
import ListAltIcon from "@mui/icons-material/ListAlt";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const Charts = (props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [totale, setTotale] = useState(0);
  const [pOK, setPOk] = useState(0);
  const [pScaduti, setPScaduti] = useState(0);
  const [pMancanti, setPMancanti] = useState(0);
  const [pScadenza, setPScadenza] = useState(0);
  const [pParziale, setPParziale] = useState(0);
  const [data, setData] = useState([]);
  const [medTotals, setMedTotals] = useState(0);
  const [medOk, setMedOk] = useState(0);
  const [medScaduti, setMedScaduti] = useState(0);
  const [medMancanti, setMedMancanti] = useState(0);
  const [medScadenza, setMedScadenza] = useState(0);
  const [medParziale, setMedParziale] = useState(0);
  const [medData, setMedData] = useState([]);
  const [user, setUser] = useState({});
  const CACHE_TTL_MS = 60 * 1000;
  const cacheGet = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) {
        sessionStorage.removeItem(key);
        return null;
      }
      return parsed.data;
    } catch {
      return null;
    }
  };

  const cacheSet = (key, data) => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch {
      // ignore cache errors
    }
  };
  useEffect(() => {
    let isMounted = true;

    const safeSet = (fn) => {
      if (isMounted) {
        fn();
      }
    };

    const fetchData = async () => {
      try {
        if (props.user && Object.keys(props.user).length > 0) {
          safeSet(() => setUser(props.user));
          await loadData(props.user, safeSet);
          return;
        }

        const logPerf = process.env.NODE_ENV !== "production";
        const tUser = logPerf ? performance.now() : 0;
        let response = await unsplash.get("utenti", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        });
        if (logPerf) {
          console.log("charts:fetchUser:", performance.now() - tUser, "ms");
        }

        const matchedUser = response.data.data.find(
          (utente) => sessionStorage.getItem("username") === utente.username,
        );

        safeSet(() => setUser(matchedUser ?? {}));

        if (matchedUser) {
          await loadData(matchedUser, safeSet);
        }
      } catch (error) {
        console.error(error.message);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [props.user]);

  const loadData = async (user, safeSet = (fn) => fn()) => {
    try {
      const isSicurezzaOrFormazione = Boolean(
        props.Sicurezza || user?.Formazione || user?.Sicurezza,
      );
      const isMedicina = Boolean(user?.Medicina || user?.tipologia?.Medicina);

      const requests = [];

      if (isSicurezzaOrFormazione) {
        const cached = cacheGet("charts:sicurezzaPercentuali");
        if (cached) {
          safeSet(() => {
            setData(cached.data);
            setTotale(cached.totale);
            setPOk(cached.POk);
            setPScadenza(cached.Pscadenza);
            setPScaduti(cached.Pscaduto);
            setPMancanti(cached.Pmancante);
          });
        }
        requests.push(
          (async () => {
            const logPerf = process.env.NODE_ENV !== "production";
            const t = logPerf ? performance.now() : 0;
            const res = await unsplash.get("analysis", {
              params: { operazione: "sicurezzaPercentuali" },
              headers: {
                Authorization: `Bearer ${localStorage.getItem("jwt")}`,
              },
            });
            if (logPerf) {
              console.log(
                "charts:sicurezzaPercentuali:",
                performance.now() - t,
                "ms",
              );
            }
            return res;
          })(),
        );
      }

      if (isMedicina) {
        const cached = cacheGet("charts:medicinaPercentuali");
        if (cached) {
          safeSet(() => {
            setMedData(cached.data);
            setMedTotals(cached.totale);
            setMedOk(cached.POk);
            setMedScadenza(cached.Pscadenza);
            setMedScaduti(cached.Pscaduto);
            setMedMancanti(cached.Pmancante);
            setMedParziale(cached.Pparziale);
          });
        }
        requests.push(
          (async () => {
            const logPerf = process.env.NODE_ENV !== "production";
            const t = logPerf ? performance.now() : 0;
            const res = await unsplash.get("analysis", {
              params: {
                operazione: "medicinaPercentuali",
                user: sessionStorage.getItem("idUsername"),
              },
              headers: {
                Authorization: `Bearer ${localStorage.getItem("jwt")}`,
              },
            });
            if (logPerf) {
              console.log(
                "charts:medicinaPercentuali:",
                performance.now() - t,
                "ms",
              );
            }
            return res;
          })(),
        );
      }

      const responses = await Promise.all(requests);
      let index = 0;

      if (isSicurezzaOrFormazione) {
        const response = responses[index++];
        safeSet(() => {
          setData(response.data.data.data);
          setTotale(response.data.data.totale);
          setPOk(response.data.data.POk);
          setPScadenza(response.data.data.Pscadenza);
          setPScaduti(response.data.data.Pscaduto);
          setPMancanti(response.data.data.Pmancante);
        });
        cacheSet("charts:sicurezzaPercentuali", response.data.data);
      }

      if (isMedicina) {
        const response = responses[index++];
        safeSet(() => {
          setMedData(response.data.data.data);
          setMedTotals(response.data.data.totale);
          setMedOk(response.data.data.POk);
          setMedScadenza(response.data.data.Pscadenza);
          setMedScaduti(response.data.data.Pscaduto);
          setMedMancanti(response.data.data.Pmancante);
          setMedParziale(response.data.data.Pparziale);
        });
        cacheSet("charts:medicinaPercentuali", response.data.data);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      fill,
      payload,
      percent,
      value,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? "start" : "end";

    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={10}
          textAnchor="middle"
          fill={fill}
          fontSize={25}
        >
          {payload.value}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
      </g>
    );
  };

  const renderChartCard = ({
    title,
    totals,
    ok,
    scadenza,
    scaduti,
    mancanti,
    parziale,
    chartData,
    showParziale,
  }) => {
    const legendItems = [
      { label: "OK", color: "#40C18F", value: ok },
      { label: "In Scadenza", color: "#FFC897", value: scadenza },
      { label: "Scaduti", color: "#FF2768", value: scaduti },
      { label: "Mancante", color: "#8E75AC", value: mancanti },
      ...(showParziale
        ? [{ label: "Parziali", color: "#2265B1", value: parziale }]
        : []),
    ].filter((item) => Number(item.value) > 0);

    return (
      <Card
        sx={{
          boxShadow: "none",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
        }}
      >
        <CardContent sx={{ paddingTop: "10px" }}>
          <Grid container spacing={2} sx={{ p: 0 }}>
            <Grid item xs={12} lg={4} sx={{ width: "250px", height: "250px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <div className="flex items-center justify-center h-full">
                  {chartData.length > 0 ? (
                    <PieChart width={250} height={250}>
                      <Pie
                        activeIndex={activeIndex}
                        activeShape={renderActiveShape}
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        onMouseEnter={onPieEnter}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  ) : (
                    <CircularProgress
                      size={80}
                      thickness={5}
                      sx={{ color: "#2265B1" }}
                    />
                  )}
                </div>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} lg={8} className="flex flex-wrap items-center">
              <div className="flex flex-no-wrap items-center">
                <ListAltIcon sx={{ fontSize: 55, color: "#11526F" }} />
                <div style={{ marginLeft: "5px" }} className="flex flex-wrap">
                  <span className="w-full fw-light fs-6">{title}</span>
                  <h6 className="fs-5 fw-bold" style={{ color: "#11526F" }}>
                    {totals ?? "0000"}
                  </h6>
                </div>
              </div>
              <Divider sx={{ color: "rgba(58, 53, 65, 0.6)", width: "100%" }} />
              <div className="flex flex-wrap" style={{ padding: "15px 0" }}>
                {legendItems.map((item) => (
                  <div
                    key={item.label}
                    className="w-1/2 flex flex-wrap items-center"
                  >
                    <Circle sx={{ fontSize: 40, color: item.color }} />
                    <div
                      style={{ marginLeft: "5px" }}
                      className="flex flex-wrap"
                    >
                      <span
                        style={{
                          float: "left",
                          marginRight: "5px",
                          width: "100%",
                        }}
                      >
                        {` ${item.label}`}
                      </span>
                      <span
                        className="fs-6 fw-semibold"
                        style={{ color: "#11526F" }}
                      >
                        {item.value + "%"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const isSicurezzaOrFormazione = Boolean(
    props.Sicurezza || user?.Formazione || user?.Sicurezza,
  );
  const isMedicina = Boolean(user?.Medicina || user?.tipologia?.Medicina);

  if (isSicurezzaOrFormazione && isMedicina) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          {renderChartCard({
            title: "Corsi totali",
            totals: totale,
            ok: pOK,
            scadenza: pScadenza,
            scaduti: pScaduti,
            mancanti: pMancanti,
            parziale: pParziale,
            chartData: data,
            showParziale: false,
          })}
        </Grid>
        <Grid item xs={12}>
          {renderChartCard({
            title: "Visite totali",
            totals: medTotals,
            ok: medOk,
            scadenza: medScadenza,
            scaduti: medScaduti,
            mancanti: medMancanti,
            parziale: medParziale,
            chartData: medData,
            showParziale: true,
          })}
        </Grid>
      </Grid>
    );
  }

  if (isMedicina) {
    return renderChartCard({
      title: "Visite totali",
      totals: medTotals,
      ok: medOk,
      scadenza: medScadenza,
      scaduti: medScaduti,
      mancanti: medMancanti,
      parziale: medParziale,
      chartData: medData,
      showParziale: true,
    });
  }

  return renderChartCard({
    title: "Corsi totali",
    totals: totale,
    ok: pOK,
    scadenza: pScadenza,
    scaduti: pScaduti,
    mancanti: pMancanti,
    parziale: pParziale,
    chartData: data,
    showParziale: false,
  });
};

export default Charts;
